export type LogLevel = "debug" | "info" | "warn" | "error" | "silent";

const LEVEL_RANK: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
};

const STORAGE_KEY = "ai-ui:debug";
const LEVEL_KEY = "ai-ui:log-level";

declare global {
  interface Window {
    __MAINTAINER_PRO__?: {
      debug?: boolean | string;
      logLevel?: string;
      [key: string]: unknown;
    };
    __AI_UI_DEBUG__?: boolean | string;
  }
}

function readQueryFlag(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URLSearchParams(window.location.search).get("aiUiDebug");
    if (v === null) return null;
    if (v === "0" || v === "false") return false;
    return true;
  } catch {
    return null;
  }
}

function readStorageFlag(): boolean | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === null) return null;
    if (v === "0" || v === "false") return false;
    return true;
  } catch {
    return null;
  }
}

function isLocalHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host.endsWith(".local")
  );
}

/** Whether verbose ai-ui logging is enabled. */
export function isAiUiDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const query = readQueryFlag();
  if (query !== null) return query;
  const storage = readStorageFlag();
  if (storage !== null) return storage;
  const win = window.__AI_UI_DEBUG__;
  if (win === true || win === "1" || win === "true") return true;
  if (win === false || win === "0" || win === "false") return false;
  const cfg = window.__MAINTAINER_PRO__?.debug;
  if (cfg === true || cfg === "1" || cfg === "true") return true;
  if (cfg === false || cfg === "0" || cfg === "false") return false;
  // Local embeds default to debug so localhost testing is noisy by default.
  return isLocalHost();
}

export function resolveAiUiLogLevel(): LogLevel {
  if (typeof window !== "undefined") {
    try {
      const fromStorage = window.localStorage.getItem(LEVEL_KEY);
      if (fromStorage && fromStorage in LEVEL_RANK) {
        return fromStorage as LogLevel;
      }
    } catch {
      /* ignore */
    }
    const fromCfg = window.__MAINTAINER_PRO__?.logLevel;
    if (typeof fromCfg === "string" && fromCfg in LEVEL_RANK) {
      return fromCfg as LogLevel;
    }
  }
  return isAiUiDebugEnabled() ? "debug" : "info";
}

/**
 * Persist debug on/off (and optionally level) in localStorage.
 * Also usable from the console: `AiUi.setDebug(true)`.
 */
export function setAiUiDebug(enabled: boolean, level?: LogLevel): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? "1" : "0");
    if (level) window.localStorage.setItem(LEVEL_KEY, level);
    window.__AI_UI_DEBUG__ = enabled;
  } catch {
    /* ignore */
  }
}

export function previewText(
  text: string | undefined | null,
  max = 160
): string {
  const t = String(text ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!t) return "";
  return t.length <= max ? t : `${t.slice(0, max)}…`;
}

type LogFn = (metaOrMsg?: unknown, msg?: string) => void;

export type AiUiLogger = {
  debug: LogFn;
  info: LogFn;
  warn: LogFn;
  error: LogFn;
  child: (name: string) => AiUiLogger;
};

function emit(
  name: string,
  level: LogLevel,
  metaOrMsg?: unknown,
  msg?: string
) {
  const min = resolveAiUiLogLevel();
  if (LEVEL_RANK[level] < LEVEL_RANK[min]) return;

  const prefix = `[ai-ui:${name}]`;
  const hasMsg = typeof msg === "string";
  const message = hasMsg ? msg : typeof metaOrMsg === "string" ? metaOrMsg : "";
  const meta = hasMsg
    ? metaOrMsg
    : typeof metaOrMsg === "string"
      ? undefined
      : metaOrMsg;

  const args =
    meta !== undefined && message
      ? [prefix, message, meta]
      : meta !== undefined
        ? [prefix, meta]
        : [prefix, message || level];

  if (level === "error") console.error(...args);
  else if (level === "warn") console.warn(...args);
  else if (level === "info") console.info(...args);
  // Use log (not debug) so Chrome's default filter shows verbose lines.
  else console.log(...args);
}

export function createAiUiLogger(name = "app"): AiUiLogger {
  return {
    debug: (metaOrMsg, msg) => emit(name, "debug", metaOrMsg, msg),
    info: (metaOrMsg, msg) => emit(name, "info", metaOrMsg, msg),
    warn: (metaOrMsg, msg) => emit(name, "warn", metaOrMsg, msg),
    error: (metaOrMsg, msg) => emit(name, "error", metaOrMsg, msg),
    child: (childName) => createAiUiLogger(`${name}:${childName}`),
  };
}

export const log = createAiUiLogger("app");
